import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuthUnified } from '@/hooks/shared/useAuthUnified';
import { LogoWithDomainSwitcher } from '@/shared/components/layout/LogoWithDomainSwitcher';
import {
  ChevronRight,
  ChevronLeft,
  X,
  TrendingUp,
  Activity,
  Mountain
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ForwardRefExoticComponent<any>;
  shortName?: string;
}

/**
 * GoalsSidebar component - Navigation sidebar for goals domain
 * Responsive design with mobile support
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const { hasGroupRouteAccess } = useAuthUnified();
  const location = useLocation();

  // Navigation items for goals domain
  const getNavigationItems = (): NavigationItem[] => {
    const goalsItems: NavigationItem[] = [
      {
        name: 'Run',
        href: '/goals/critical-success-factors',
        icon: Activity,
        shortName: 'Critical Success Factors'
      },
      {
        name: 'Transform',
        href: '/goals/company-initiatives',
        icon: Mountain,
        shortName: 'Company Initiatives'
      },
      {
        name: 'Grow',
        href: '/goals/department-initiatives',
        icon: TrendingUp,
        shortName: 'Department Initiatives'
      }
    ];

    return goalsItems.filter(item => hasGroupRouteAccess(item.href));
  };

  const navigationItems = getNavigationItems();

  const isActiveRoute = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Floating Container */}
      <div
        className={`fixed z-50 bg-white transform transition-all duration-300 ease-in-out
          lg:left-4 lg:top-4 lg:bottom-4 lg:rounded-2xl lg:shadow-2xl lg:translate-x-0
          inset-y-0 left-0 shadow-lg
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-72'}
        `}
      >
        {/* Collapse toggle button - Desktop only, positioned outside overflow container */}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex absolute -right-3 top-[68px] z-[60] w-6 h-6 items-center justify-center bg-white rounded-full border border-gray-200 shadow-md hover:shadow-lg hover:border-gray-300 hover:scale-110 transition-all cursor-pointer"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          type="button"
        >
          {isCollapsed ? (
            <ChevronRight className="h-3.5 w-3.5 text-gray-600" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5 text-gray-600" />
          )}
        </button>
        <div className="flex flex-col h-full lg:rounded-2xl overflow-hidden">
          {/* Sidebar header - Mobile close button only */}
          <div className="flex items-center justify-end p-4 border-b border-gray-200 lg:hidden">
            <button
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Logo with integrated Domain Switcher */}
          <LogoWithDomainSwitcher isCollapsed={isCollapsed} />

          {/* Navigation */}
          <nav className="flex-1 px-4 py-5 space-y-1 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.href);
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => {
                    console.log('[Sidebar] Navigating to:', item.href, item.name);
                    // Close mobile menu when navigating
                    if (window.innerWidth < 1024) {
                      onClose();
                    }
                  }}
                  className={`group flex items-center text-sm font-medium rounded-lg transition-all ${
                    isCollapsed ? 'px-3 py-3 justify-center' : 'px-3 py-2'
                  } ${
                    isActive
                      ? 'bg-gradient-to-r from-[#5b3d72] to-[#704a8b] text-white shadow-md'
                      : 'text-gray-600 hover:bg-[#ebe4f1]/50 hover:text-[#311c3b]'
                  }`}
                  title={item.name} // Show full name on hover when collapsed
                >
                  <Icon
                    className={`h-5 w-5 transition-colors ${
                      isCollapsed ? '' : 'mr-3'
                    } ${
                      isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#311c3b]'
                    }`}
                  />
                  {!isCollapsed && (
                    <span className="flex-1">
                      {item.shortName || item.name}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

        </div>
      </div>
    </>
  );
};

export default Sidebar;